import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Star, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import AutocompleteSelect from '../components/AutocompleteSelect';
import { getDoctors, getMe, trackEvent, getSpecialties, getCounties, getInsurances } from '../services/api';

export default function DirectoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    specialty: searchParams.get('specialty') || '',
    region: searchParams.get('region') || '',
    insurance: searchParams.get('insurance') || ''
  });
  const [specialties, setSpecialties] = useState([]);
  const [counties, setCounties] = useState([]);
  const [insurances, setInsurances] = useState([]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getMe();
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
    };
    checkAuth();
    
    // Load filter options
    const loadFilterOptions = async () => {
      try {
        const [specialtiesRes, countiesRes, insurancesRes] = await Promise.all([
          getSpecialties(),
          getCounties(),
          getInsurances()
        ]);
        setSpecialties(specialtiesRes.data);
        setCounties(countiesRes.data);
        setInsurances(insurancesRes.data);
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadDoctors();
  }, [searchParams]);

  const loadDoctors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchParams.get('search')) params.search = searchParams.get('search');
      if (searchParams.get('specialty')) params.specialty = searchParams.get('specialty');
      if (searchParams.get('region')) params.region = searchParams.get('region');
      if (searchParams.get('insurance')) params.insurance = searchParams.get('insurance');
      if (searchParams.get('featured')) params.featured = searchParams.get('featured') === 'true';

      const response = await getDoctors(params);
      setDoctors(response.data);
      
      // Track search event
      if (params.search || params.specialty || params.region) {
        await trackEvent('search', null, { query: params.search, filters: params });
      }
    } catch (error) {
      console.error('Error loading doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (filters.specialty) params.set('specialty', filters.specialty);
    if (filters.region) params.set('region', filters.region);
    if (filters.insurance) params.set('insurance', filters.insurance);
    navigate(`/directory?${params.toString()}`);
  };

  const handleDoctorClick = async (doctorId) => {
    await trackEvent('profile_view', doctorId);
    navigate(`/doctor/${doctorId}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search Header */}
        <div className="bg-white rounded-xl border border-slate-100 p-6 mb-8">
          <h1 className="font-manrope font-bold text-3xl text-[#0F2A44] mb-6">
            Find Your Doctor
          </h1>
          
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-3">
              <Input
                type="text"
                placeholder="Search by name or specialty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="directory-search-input"
                className="flex-1 h-12 rounded-lg font-public"
              />
              <Button
                type="submit"
                data-testid="directory-search-btn"
                className="h-12 px-8 bg-[#0F2A44] hover:bg-[#0F2A44]/90 text-white rounded-lg"
              >
                <Search className="h-5 w-5" />
              </Button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AutocompleteSelect
                options={specialties}
                value={filters.specialty}
                onChange={(value) => setFilters({ ...filters, specialty: value })}
                placeholder="Filter by specialty"
                dataTestId="filter-specialty"
              />
              <AutocompleteSelect
                options={counties}
                value={filters.region}
                onChange={(value) => setFilters({ ...filters, region: value })}
                placeholder="Filter by county"
                dataTestId="filter-region"
              />
              <AutocompleteSelect
                options={insurances}
                value={filters.insurance}
                onChange={(value) => setFilters({ ...filters, insurance: value })}
                placeholder="Filter by insurance"
                dataTestId="filter-insurance"
              />
            </div>
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
          </div>
        ) : doctors.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
            <p className="font-public text-[#4B5563]">No doctors found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="font-public text-sm text-[#4B5563]">
              Found {doctors.length} doctor{doctors.length !== 1 ? 's' : ''}
            </p>
            
            {doctors.map((doctor) => (
              <div
                key={doctor.doctor_id}
                onClick={() => handleDoctorClick(doctor.doctor_id)}
                className="bg-white rounded-xl border border-slate-100 p-6 cursor-pointer hover-lift"
                data-testid={`doctor-card-${doctor.doctor_id}`}
              >
                <div className="flex items-start gap-6">
                  {doctor.photo_url ? (
                    <img
                      src={doctor.photo_url}
                      alt={doctor.name}
                      className="w-24 h-24 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-slate-200 flex items-center justify-center">
                      <span className="text-3xl text-slate-500 font-manrope font-bold">
                        {doctor.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-manrope font-bold text-xl text-[#0F2A44]">
                          {doctor.title} {doctor.name}
                        </h3>
                        <p className="font-public text-[#4B5563]">{doctor.specialty}</p>
                      </div>
                      
                      {doctor.is_featured && (
                        <span className="px-3 py-1 bg-[#2DD4BF]/10 text-[#2DD4BF] text-xs font-medium rounded-full">
                          Featured
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm mb-3">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" />
                        <span className="font-space font-medium text-[#262626]">
                          {doctor.average_rating.toFixed(1)}
                        </span>
                        <span className="text-[#4B5563] font-public">
                          ({doctor.total_reviews} reviews)
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-[#4B5563]" />
                        <span className="text-[#4B5563] font-public">{doctor.region}</span>
                      </div>
                    </div>
                    
                    {doctor.bio && (
                      <p className="font-public text-sm text-[#4B5563] line-clamp-2">
                        {doctor.bio}
                      </p>
                    )}
                    
                    {doctor.insurances_accepted && doctor.insurances_accepted.length > 0 && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {doctor.insurances_accepted.slice(0, 3).map((insurance, idx) => (
                          <span key={idx} className="px-2 py-1 bg-slate-100 text-[#4B5563] text-xs rounded font-public">
                            {insurance}
                          </span>
                        ))}
                        {doctor.insurances_accepted.length > 3 && (
                          <span className="px-2 py-1 text-[#4B5563] text-xs font-public">
                            +{doctor.insurances_accepted.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}